import React, { useMemo } from 'react';
import intl from 'react-intl-universal';
import { Dropdown, FormItem, Typography } from '@getsynapse/design-system';
import { Option } from 'utils/customTypes';
import { TaskTemplate } from 'utils/types/taskTemplate';
import { DependenciesMap, RelationshipType } from './types';

interface DependenciesSectionProps {
  dependencies: DependenciesMap;
  onChange: (type: RelationshipType, taskIds: string[]) => void;
  availableTasks: TaskTemplate[];
}

const DependenciesSection: React.FC<DependenciesSectionProps> = ({
  dependencies,
  onChange,
  availableTasks,
}) => {
  const taskOptions = useMemo(
    () => availableTasks.map((task) => ({ value: task.id, label: task.name })),
    [availableTasks]
  );

  const selectedPredecessors = useMemo(
    () =>
      (dependencies.depends_on ?? [])
        .map((id) => taskOptions.find((t) => t.value === id))
        .filter((option): option is Option => option !== undefined),
    [dependencies.depends_on, taskOptions]
  );

  const selectedSuccessors = useMemo(
    () =>
      (dependencies.blocks ?? [])
        .map((id) => taskOptions.find((t) => t.value === id))
        .filter((option): option is Option => option !== undefined),
    [dependencies.blocks, taskOptions]
  );

  const availablePredecessorOptions = useMemo(() => {
    const successorIds = new Set(dependencies.blocks ?? []);
    return taskOptions.filter((option) => !successorIds.has(option.value));
  }, [taskOptions, dependencies.blocks]);

  const availableSuccessorOptions = useMemo(() => {
    const predecessorIds = new Set(dependencies.depends_on ?? []);
    return taskOptions.filter((option) => !predecessorIds.has(option.value));
  }, [taskOptions, dependencies.depends_on]);

  const handleChange = (type: RelationshipType) => (options: Option[]) => {
    onChange(
      type,
      options.map((o) => o.value)
    );
  };

  return (
    <div className='flex flex-col gap-6'>
      <Typography variant='body' className='text-primary'>
        {intl.get(
          'SETTINGS_PAGE.TASK_BUNDLE_PAGE.DEPENDENCIES.SECTION_DESCRIPTION'
        )}
      </Typography>

      <FormItem
        label={intl.get(
          'SETTINGS_PAGE.TASK_BUNDLE_PAGE.DEPENDENCIES.RELATIONSHIP_DEPENDS_ON'
        )}
      >
        <Dropdown
          multiple
          filterable
          options={availablePredecessorOptions}
          onChange={handleChange('depends_on')}
          values={selectedPredecessors}
          disabled={availablePredecessorOptions.length === 0}
          triggerProps={{
            placeholder: intl.get(
              'SETTINGS_PAGE.TASK_BUNDLE_PAGE.DEPENDENCIES.SEARCH_PLACEHOLDER'
            ),
            className: 'py-1',
          }}
        />
      </FormItem>

      <FormItem
        label={intl.get(
          'SETTINGS_PAGE.TASK_BUNDLE_PAGE.DEPENDENCIES.RELATIONSHIP_BLOCKS'
        )}
      >
        <Dropdown
          multiple
          filterable
          options={availableSuccessorOptions}
          onChange={handleChange('blocks')}
          values={selectedSuccessors}
          disabled={availableSuccessorOptions.length === 0}
          triggerProps={{
            placeholder: intl.get(
              'SETTINGS_PAGE.TASK_BUNDLE_PAGE.DEPENDENCIES.SEARCH_PLACEHOLDER'
            ),
            className: 'py-1',
          }}
        />
      </FormItem>
    </div>
  );
};

export default DependenciesSection;
